/**
 * Turns the stored files of one Zoom cloud recording into the
 * {@link ShadowSegment}s the recording viewers play: videos grouped by
 * recording block, most useful view first, each block with its transcript.
 * Storage access is passed in, so section and office hours recordings, which
 * live under different paths, share the grouping.
 */
import {z} from 'zod';

import {getTranscriptEntries} from '../../../section/transcriptsApi';
import type {
  ShadowChatLine,
  ShadowSegment,
  ShadowTranscriptLine,
  ShadowVideo,
} from './shadowFeedback';

/** The fields of a stored recording file that segments are built from. */
export interface RecordingSegmentRendering {
  /** Zoom `file_type`, e.g. `MP4`, `TRANSCRIPT`. */
  fileType: string;
  /** Zoom `recording_type` (e.g. `shared_screen`); null when unknown. */
  recordingType: string | null;
  /** ISO 8601 start of the file's recording block; null if absent. */
  recordingStart: string | null;
  /** ISO 8601 end of the file's recording block; null if absent. */
  recordingEnd: string | null;
}

/** Zoom `recording_type` values in the order they are offered. */
const RECORDING_TYPE_ORDER = [
  'shared_screen',
  'active_speaker',
  'gallery_view',
];

const RECORDING_TYPE_LABELS: Record<string, string> = {
  shared_screen: 'Screen share',
  active_speaker: 'Active speaker',
  gallery_view: 'Gallery',
};

/** Viewer-facing label for a video rendering. */
function labelForRecordingType(
  recordingType: string | null,
  index: number,
): string {
  if (!recordingType) return `Recording ${String(index + 1)}`;
  return (
    RECORDING_TYPE_LABELS[recordingType] ?? recordingType.replace(/_/g, ' ')
  );
}

/**
 * Orders two renderings by view: screen share, then speaker, then gallery,
 * then anything unlabelled.
 *
 * @param a - A rendering.
 * @param b - Another rendering.
 * @return Negative when `a` is offered first, positive when `b` is, else 0.
 * @throws Never.
 */
export function compareRecordingTypes(
  a: Pick<RecordingSegmentRendering, 'recordingType'>,
  b: Pick<RecordingSegmentRendering, 'recordingType'>,
): number {
  const rank = (recordingType: string | null) => {
    const index = RECORDING_TYPE_ORDER.indexOf(recordingType ?? '');
    return index === -1 ? RECORDING_TYPE_ORDER.length : index;
  };
  return rank(a.recordingType) - rank(b.recordingType);
}

/**
 * Groups renderings by recording block. A host who drops and rejoins splits a
 * meeting into blocks, each with its own videos and its own transcript whose
 * timestamps count from that block's start. Renderings with no start (older
 * recordings) fall into one block.
 *
 * @param renderings - Every stored file of the recording.
 * @return The blocks, earliest first.
 * @throws Never.
 */
export function groupRecordingSegments<
  T extends Pick<RecordingSegmentRendering, 'recordingStart'>,
>(renderings: readonly T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const rendering of renderings) {
    const key = rendering.recordingStart ?? '';
    const group = groups.get(key);
    if (group) {
      group.push(rendering);
    } else {
      groups.set(key, [rendering]);
    }
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, group]) => group);
}

/**
 * Parses a WebVTT transcript into lines, marking the speaker with the most
 * lines as the one leading the session.
 *
 * @param vttContent - The WebVTT text.
 * @return The lines in order.
 * @throws Never.
 */
export function toRecordingTranscriptLines(
  vttContent: string,
): ShadowTranscriptLine[] {
  const entries = getTranscriptEntries(vttContent);
  const counts = new Map<string, number>();
  for (const entry of entries) {
    counts.set(entry.speaker, (counts.get(entry.speaker) ?? 0) + 1);
  }
  let leader = '';
  let max = -1;
  for (const [speaker, count] of counts) {
    if (count > max) {
      max = count;
      leader = speaker;
    }
  }
  return entries.map(entry => ({
    speaker: entry.speaker,
    isLeader: entry.speaker === leader,
    timestamp: entry.startTime,
    text: entry.text,
  }));
}

/**
 * Chat line headers, by Zoom layout. "From" is the local-save layout
 * (`00:01:02 From Ada to Everyone:`), which also names the recipient; plain is
 * the cloud recording layout (`00:01:02<TAB>Ada:<TAB>hi`).
 */
const FROM_HEADER =
  /^(\d{1,2}:\d{2}:\d{2})\s+From\s+(.+?)(?:\s+to\s+(.+?))?\s*:(?:\s?(.*))?$/i;
const PLAIN_HEADER = /^(\d{1,2}:\d{2}:\d{2})\s+([^\t]+?):(?:\s(.*))?$/;
/** A plain-layout sender naming a private recipient, as the "From" layout does. */
const PLAIN_DIRECT_SENDER = /\sto\s.+\((direct message|privately)\)$/i;

/**
 * One message of the JSON-lines chat layout, keeping only the fields read.
 * Names and text are base64 UTF-8.
 */
const ChatJsonRecordSchema = z.object({
  time_from_recording_start: z.string(),
  s_name: z.string(),
  content: z.string(),
});

/** A chat message while its continuation lines are still being read. */
interface PendingChatMessage {
  sender: string;
  timestamp: string;
  text: string[];
}

/** Decodes base64 UTF-8, or returns the value unchanged if it is not base64. */
function decodeBase64Text(value: string): string {
  try {
    const binary = atob(value);
    return new TextDecoder().decode(
      Uint8Array.from(binary, char => char.charCodeAt(0)),
    );
  } catch {
    // Not base64: the field already holds plain text.
    return value;
  }
}

/** A recipient that means the whole meeting, e.g. "Everyone in Meeting". */
function isEveryone(recipient: string): boolean {
  return (
    /^everyone\b/i.test(recipient.trim()) &&
    !/direct message|privately/i.test(recipient)
  );
}

/** Parses one JSON-lines record; null for the header or anything else. */
function parseJsonChatRecord(line: string): PendingChatMessage | null {
  let value: unknown;
  try {
    value = JSON.parse(line);
  } catch {
    // A line that only looks like JSON is not a message.
    return null;
  }
  const record = ChatJsonRecordSchema.safeParse(value);
  if (!record.success) return null;
  return newMessage({
    sender: decodeBase64Text(record.data.s_name),
    timestamp: record.data.time_from_recording_start,
    text: decodeBase64Text(record.data.content),
  });
}

/** Starts a message from its header's fields. */
function newMessage({
  sender,
  timestamp,
  text,
}: {
  sender: string;
  timestamp: string;
  text: string | undefined;
}): PendingChatMessage {
  return {
    sender: sender.trim(),
    timestamp,
    text: text?.trim() ? [text.trimEnd()] : [],
  };
}

/**
 * Parses one header line of the "From" or plain layout.
 *
 * @return The message it starts, null for a direct message, or undefined when
 *   the line is not a header.
 */
function parseTextChatHeader(
  line: string,
): PendingChatMessage | null | undefined {
  const from = FROM_HEADER.exec(line);
  if (from) {
    // Optional groups are undefined when unmatched; indexing hides that.
    const recipient = from.at(3);
    if (recipient !== undefined && !isEveryone(recipient)) return null;
    return newMessage({sender: from[2], timestamp: from[1], text: from.at(4)});
  }
  const plain = PLAIN_HEADER.exec(line);
  if (!plain) return undefined;
  if (PLAIN_DIRECT_SENDER.test(plain[2])) return null;
  return newMessage({sender: plain[2], timestamp: plain[1], text: plain.at(3)});
}

/** What a chat file parsed to. */
export interface ParsedRecordingChat {
  /** Messages sent to everyone, in file order. */
  chat: ShadowChatLine[];
  /**
   * Lines read as a message header or record, including dropped direct
   * messages and the JSON layout's file header. Zero means the file's layout
   * was not recognised.
   */
  recognizedLines: number;
}

/**
 * Parses a Zoom chat file into the messages sent to everyone. Reads both cloud
 * recording layouts (tab-separated text, and JSON lines) and the local-save
 * "From" layout. A message the file addresses to anyone but everyone (a
 * direct message) is dropped; the cloud layouts should name no recipient
 * because Zoom saves only messages to everyone in them.
 *
 * @param chatContent - The chat file's text.
 * @param leader - Display name of the session's leader, which marks their
 *   messages; omitted, no message is marked.
 * @return The messages, and how many lines were recognised. A multi-line
 *   message keeps its line breaks and the indentation of its continuation
 *   lines.
 * @throws Never.
 */
export function parseRecordingChat(
  chatContent: string,
  leader?: string,
): ParsedRecordingChat {
  const lines = chatContent.replace(/^﻿/, '').split(/\r?\n/);
  // Only a JSON-lines file starts with a JSON record; in a text file a line
  // starting with a brace is pasted code.
  const isJsonLayout =
    lines
      .find(line => line.trim())
      ?.trimStart()
      .startsWith('{') ?? false;
  const messages: PendingChatMessage[] = [];
  let recognizedLines = 0;
  let current: PendingChatMessage | null = null;
  for (const rawLine of lines) {
    const header = isJsonLayout
      ? parseJsonChatRecord(rawLine)
      : parseTextChatHeader(rawLine);
    if (header !== undefined) {
      recognizedLines++;
      current = header;
      if (current) messages.push(current);
      continue;
    }
    // Zoom indents continuation lines with one tab; what follows is the
    // message's own indentation, such as pasted code.
    const continuation = rawLine.replace(/^\t/, '').trimEnd();
    if (current && continuation.trim()) current.text.push(continuation);
  }
  const chat = messages
    .map(message => ({
      sender: message.sender,
      isLeader: leader !== undefined && message.sender === leader,
      timestamp: message.timestamp,
      text: message.text.join('\n'),
    }))
    .filter(message => message.text);
  return {chat, recognizedLines};
}

/** Seconds between two ISO 8601 instants; null when either is unusable. */
function durationSeconds(
  start: string | null,
  end: string | null,
): number | null {
  if (!start || !end) return null;
  const seconds = (Date.parse(end) - Date.parse(start)) / 1000;
  return Number.isNaN(seconds) || seconds < 0 ? null : seconds;
}

/**
 * Builds a recording's segments: per block, a signed URL for each MP4 (most
 * useful view first), the block's parsed transcript and, when `readChat` is
 * given, its chat. Does no authorization; callers must clear the viewer first.
 *
 * @param params.renderings - Every stored file of the recording.
 * @param params.signVideo - Returns a short-lived URL to an MP4 rendering.
 * @param params.readTranscript - Returns a `TRANSCRIPT` rendering's WebVTT.
 * @param params.readChat - Returns a `CHAT` rendering's text; omitted, chat
 *   is not read and segments carry no `chat` field.
 * @param params.onError - Told of each block whose videos, transcript or chat
 *   could not be loaded; that part of the block is then left empty.
 * @return The segments, earliest first, without blocks that have neither a
 *   video nor a transcript line.
 * @throws Never; storage failures go to `onError`.
 */
export async function buildRecordingSegments<
  T extends RecordingSegmentRendering,
>({
  renderings,
  signVideo,
  readTranscript,
  readChat,
  onError,
}: {
  renderings: readonly T[];
  signVideo: (rendering: T) => Promise<string>;
  readTranscript: (rendering: T) => Promise<string>;
  readChat?: (rendering: T) => Promise<string>;
  onError: (params: {
    part: 'videos' | 'transcript' | 'chat';
    recordingStart: string | null;
    error: unknown;
  }) => void;
}): Promise<ShadowSegment[]> {
  const segments = await Promise.all(
    groupRecordingSegments(renderings).map(async group => {
      const recordingStart =
        group.find(rendering => rendering.recordingStart)?.recordingStart ??
        null;
      const recordingEnd =
        group.find(rendering => rendering.recordingEnd)?.recordingEnd ?? null;

      let videos: ShadowVideo[] = [];
      try {
        const mp4s = group
          .filter(rendering => rendering.fileType === 'MP4')
          .sort(compareRecordingTypes);
        videos = await Promise.all(
          mp4s.map(async (rendering, index) => ({
            label: labelForRecordingType(rendering.recordingType, index),
            url: await signVideo(rendering),
            recordingType: rendering.recordingType,
            recordingStart: rendering.recordingStart,
          })),
        );
      } catch (error) {
        onError({part: 'videos', recordingStart, error});
      }

      let transcript: ShadowTranscriptLine[] = [];
      try {
        const transcriptRendering = group.find(
          rendering => rendering.fileType === 'TRANSCRIPT',
        );
        if (transcriptRendering) {
          transcript = toRecordingTranscriptLines(
            await readTranscript(transcriptRendering),
          );
        }
      } catch (error) {
        onError({part: 'transcript', recordingStart, error});
      }

      const segment: ShadowSegment = {
        recordingStart,
        durationSeconds: durationSeconds(recordingStart, recordingEnd),
        videos,
        transcript,
      };
      if (!readChat) return segment;

      const leader = transcript.find(line => line.isLeader)?.speaker;
      let chat: ShadowChatLine[] = [];
      try {
        const chatRendering = group.find(
          rendering => rendering.fileType === 'CHAT',
        );
        if (chatRendering) {
          const chatContent = await readChat(chatRendering);
          const parsed = parseRecordingChat(chatContent, leader);
          chat = parsed.chat;
          if (parsed.recognizedLines === 0 && chatContent.trim()) {
            // A layout the parser does not know would otherwise read as no chat.
            onError({
              part: 'chat',
              recordingStart,
              error: new Error(
                `Chat file has ${String(chatContent.split('\n').length)} lines in an unrecognised layout`,
              ),
            });
          }
        }
      } catch (error) {
        onError({part: 'chat', recordingStart, error});
      }
      return {...segment, chat};
    }),
  );
  return segments.filter(
    segment => segment.videos.length > 0 || segment.transcript.length > 0,
  );
}
